// Recolours the whole app's brand purple to the active entity's colour.
//
// The brand purple (#7C5CFC and its tints) is written directly into hundreds of styles, so instead of editing each
// one this walks the page's style sheets and swaps those exact colours for the entity's colour and its matching
// tints. The original value of every changed declaration is remembered, so switching entities always works from the
// original purple, and styles added later (a page, a dialog) are recoloured as they are inserted.

const BASE = '#7C5CFC';
// Every purple the app uses. Each one is re-derived from the entity colour at the same lightness.
const PURPLES = [
    '#7C5CFC', '#6246E0', '#9B87FB', '#8F7BF0', '#B9A9F7', '#C4B5FD', '#C9BEFB', '#DDD5FB', '#E4DEFB',
    '#E7DFFC', '#E9E3FD', '#EDE9FE', '#F1EEFE', '#F3F0FE', '#F5F3FF', '#F8F7FF',
];

const toRgb = (hex) => {
    const h = hex.replace('#', '');
    return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
};
const avg = ([r, g, b]) => (r + g + b) / 3;

// The same shade of `color` as `purple` is of the base purple: lighter tints blend towards white, darker ones towards black.
const shade = (purple, color) => {
    const p = toRgb(purple);
    const b = toRgb(BASE);
    const c = toRgb(color);
    const w = avg(p) >= avg(b) ? (avg(p) - avg(b)) / (255 - avg(b)) : (avg(p) - avg(b)) / avg(b);
    return c.map((v) => Math.round(w >= 0 ? v + (255 - v) * w : v + v * w));
};

let table = []; // [{ re, to: 'r, g, b' }] for the current colour; empty = original purple
const regexFor = ([r, g, b]) => new RegExp(`rgb(a?)\\(\\s*${r}\\s*,\\s*${g}\\s*,\\s*${b}\\s*`, 'gi');
const SOURCES = PURPLES.map((p) => regexFor(toRgb(p)));
const mentionsPurple = (v) => SOURCES.some((re) => { re.lastIndex = 0; return re.test(v); });

const swap = (value) => {
    let out = value;
    PURPLES.forEach((p, i) => {
        const t = table[i];
        if (!t) return;
        const re = regexFor(toRgb(p));
        out = out.replace(re, (_, a) => `rgb${a}(${t.join(', ')}`);
    });
    return out;
};

const originals = new WeakMap(); // style declaration -> Map(property -> original value)

const processStyle = (style) => {
    if (!style) return;
    for (let i = 0; i < style.length; i += 1) {
        const prop = style[i];
        const cur = style.getPropertyValue(prop);
        const known = originals.get(style);
        const orig = known?.has(prop) ? known.get(prop) : cur;
        if (!known?.has(prop) && !mentionsPurple(cur)) continue; // not a brand colour, leave it alone
        const next = swap(orig);
        if (next === cur) continue;
        if (!originals.has(style)) originals.set(style, new Map());
        originals.get(style).set(prop, orig);
        style.setProperty(prop, next, style.getPropertyPriority(prop));
    }
};

// Things that show an entity's own colour (its tile in the switcher, the colour swatches) carry data-keep-colour and
// must always show that colour, even when it happens to be the brand purple. Their classes are collected before each pass.
let exempt = [];
const collectExempt = () => {
    const found = new Set();
    document.querySelectorAll('[data-keep-colour]').forEach((el) => el.classList.forEach((c) => found.add(c)));
    exempt = Array.from(found, (c) => `.${c}`);
};
const isExempt = (selector) => exempt.length > 0 && typeof selector === 'string' && exempt.some((c) => selector.includes(c));

// Puts a declaration list back to the colours it had before any recolouring.
const restoreStyle = (style) => {
    const known = originals.get(style);
    if (!known) return;
    known.forEach((orig, prop) => { if (style.getPropertyValue(prop) !== orig) style.setProperty(prop, orig, style.getPropertyPriority(prop)); });
};

const processRules = (rules) => {
    if (!rules) return;
    for (let i = 0; i < rules.length; i += 1) {
        const rule = rules[i];
        if (rule.style) {
            if (isExempt(rule.selectorText)) restoreStyle(rule.style);
            else processStyle(rule.style);
        }
        if (rule.cssRules) processRules(rule.cssRules);
    }
};

const processSheet = (sheet) => {
    try { processRules(sheet.cssRules); } catch { /* a style sheet from another site can't be read */ }
};
const processAll = () => { collectExempt(); Array.from(document.styleSheets).forEach(processSheet); };

let started = false;
const start = () => {
    if (started || typeof document === 'undefined') return;
    started = true;
    // Styles inserted one by one (production) ...
    const insert = CSSStyleSheet.prototype.insertRule;
    CSSStyleSheet.prototype.insertRule = function patched(text, index) {
        const at = insert.call(this, text, index);
        if (table.length) { try { collectExempt(); processRules([this.cssRules[at]]); } catch { /* ignore */ } }
        return at;
    };
    // ... and new <style> tags (development). Emotion adds an empty <style> and then puts the rule text into it, so
    // watch the tags' contents too, not just the tags being added.
    const fix = (el) => {
        if (el?.nodeName === 'STYLE' && el.sheet) processSheet(el.sheet);
    };
    // An element that keeps its own colour can reach the page after its style was already recoloured (React may add the
    // style first and the element later), so when one appears, run a pass that puts its colours back.
    let queued = false;
    new MutationObserver((muts) => {
        if (!table.length || queued) return;
        const arrived = muts.some((m) => Array.from(m.addedNodes).some((n) => n.nodeType === 1 && (n.matches?.('[data-keep-colour]') || n.querySelector?.('[data-keep-colour]'))));
        if (!arrived) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; processAll(); });
    }).observe(document.body, { childList: true, subtree: true });

    new MutationObserver((muts) => {
        if (!table.length) return;
        const touched = new Set();
        muts.forEach((m) => {
            if (m.target?.nodeName === 'STYLE') touched.add(m.target);
            m.addedNodes.forEach((n) => { if (n.nodeName === 'STYLE') touched.add(n); });
        });
        collectExempt();
        touched.forEach(fix);
        // The sheet is rebuilt from the new text just after the change; a second pass catches any that weren't ready.
        Promise.resolve().then(() => { collectExempt(); touched.forEach(fix); });
    }).observe(document.head, { childList: true, subtree: true });
};

// Pass the entity's hex colour; pass nothing to go back to the original purple.
export const applyEntityTheme = (color) => {
    if (typeof document === 'undefined') return;
    start();
    const ok = typeof color === 'string' && /^#[0-9a-f]{6}$/i.test(color) && color.toUpperCase() !== BASE;
    table = ok ? PURPLES.map((p) => shade(p, color)) : [];
    processAll();
    // Remembered originals mean a reset puts every declaration back, so keep the table empty only after that pass.
};
